import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, Eye, FileSignature, Sparkles, X, Trash2, DollarSign, Send, MousePointerClick, AlertTriangle, Shield } from 'lucide-react';
import api from '../lib/api';

/**
 * NotificationBell
 *
 * Mantle's in-app notification center. Lives in the topbar (mobile) and
 * desktop sidebar. Polls /api/notifications/unread-count every 30 s so the
 * gold dot lights up without a page refresh. Clicking the bell opens a
 * polished popover with the latest 25 events.
 *
 * Visual language is intentionally Mantle-y:
 *   - Gold accent on unread items + a single gold dot indicator
 *   - Tinted glyph tiles per event type
 *   - Strict left-aligned typography, no chaos
 *
 * Each row is a single clean grid: icon · text · time-ago.
 * Tapping a row marks it read AND navigates to the linked entity.
 */
export default function NotificationBell({ variant = 'topbar' }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const popoverRef = useRef(null);
  const buttonRef = useRef(null);
  const navigate = useNavigate();

  const refreshCount = useCallback(async () => {
    try {
      const { data } = await api.get('/notifications/unread-count');
      setUnread(data.count || 0);
    } catch { /* silent — user just isn't authed */ }
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/notifications', { params: { limit: 25 } });
      setItems(data || []);
    } catch { setItems([]); }
    finally { setLoading(false); }
  }, []);

  // Initial fetch + 30s polling for the badge
  useEffect(() => {
    refreshCount();
    const id = setInterval(refreshCount, 30000);
    return () => clearInterval(id);
  }, [refreshCount]);

  // Load items when the popover opens
  useEffect(() => { if (open) loadList(); }, [open, loadList]);

  // Click outside / Esc to close
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (popoverRef.current?.contains(e.target)) return;
      if (buttonRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const onItemClick = async (n) => {
    // Optimistic mark-read
    if (!n.read) {
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      setUnread((c) => Math.max(0, c - 1));
      try { await api.post(`/notifications/${n.id}/read`); } catch { /* ignore */ }
    }
    if (n.link) {
      setOpen(false);
      navigate(n.link);
    }
  };

  const onMarkAllRead = async () => {
    setItems((prev) => prev.map((x) => ({ ...x, read: true })));
    setUnread(0);
    try { await api.post('/notifications/read-all'); } catch { /* ignore */ }
  };

  const onDelete = async (e, n) => {
    e.stopPropagation();
    setItems((prev) => prev.filter((x) => x.id !== n.id));
    if (!n.read) setUnread((c) => Math.max(0, c - 1));
    try { await api.delete(`/notifications/${n.id}`); } catch { /* ignore */ }
  };

  // ---- Variant-specific button styling -----------------------------------
  const isDesktopRow = variant === 'desktop-row';
  const isDrawerRow = variant === 'drawer-row';
  const isDesktopIcon = variant === 'desktop-icon';

  // ============ Trigger button ============
  let trigger;
  if (isDesktopRow) {
    trigger = (
      <button
        ref={buttonRef}
        data-testid="notification-bell"
        onClick={() => setOpen((v) => !v)}
        className={`relative flex items-center gap-2.5 px-3 py-2 w-full rounded-md text-[13px] transition-all ${
          open || unread > 0
            ? 'text-[#CBA33B] bg-white/[0.03]'
            : 'text-[#8899AA] hover:text-white hover:bg-white/[0.03]'
        }`}
      >
        <Bell size={18} className="flex-shrink-0" strokeWidth={1.8} />
        <span className="flex-1 text-left">Notifications</span>
        {unread > 0 && (
          <span className="px-1.5 min-w-[18px] h-[18px] inline-flex items-center justify-center rounded-full bg-[#CBA33B] text-[#0D1520] text-[10px] font-bold">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>
    );
  } else if (isDrawerRow) {
    trigger = (
      <button
        ref={buttonRef}
        data-testid="notification-bell"
        onClick={() => setOpen((v) => !v)}
        style={{ WebkitTapHighlightColor: 'transparent' }}
        className="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-[14px] font-medium text-slate-700 dark:text-[#C8D2DE] hover:bg-slate-100 dark:hover:bg-white/[0.04] active:scale-[0.98] transition-all"
      >
        <Bell size={20} strokeWidth={1.8} className="flex-shrink-0" />
        <span className="flex-1 text-left">Notifications</span>
        {unread > 0 && (
          <span className="px-1.5 min-w-[20px] h-5 inline-flex items-center justify-center rounded-full bg-[#CBA33B] text-[#0D1520] text-[10px] font-bold">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>
    );
  } else if (isDesktopIcon) {
    // Collapsed desktop sidebar — icon-only trigger, popover opens to the right
    trigger = (
      <button
        ref={buttonRef}
        data-testid="notification-bell"
        onClick={() => setOpen((v) => !v)}
        aria-label={`Notifications${unread ? ` (${unread} unread)` : ''}`}
        className={`relative h-10 w-10 flex items-center justify-center rounded-md transition-all ${
          open || unread > 0
            ? 'text-[#CBA33B] bg-white/[0.04]'
            : 'text-[#8899AA] hover:text-white hover:bg-white/[0.03]'
        }`}
      >
        <Bell size={18} strokeWidth={1.9} />
        {unread > 0 && (
          <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-[#CBA33B] ring-2 ring-[#0D1520]" />
        )}
      </button>
    );
  } else {
    // Default: topbar
    trigger = (
      <button
        ref={buttonRef}
        data-testid="notification-bell"
        onClick={() => setOpen((v) => !v)}
        aria-label={`Notifications${unread ? ` (${unread} unread)` : ''}`}
        className="relative h-11 w-11 flex items-center justify-center rounded-lg text-slate-600 dark:text-[#A8B8CA] hover:text-[#B98A2C] dark:hover:text-[#CBA33B] active:bg-slate-100 dark:active:bg-white/[0.06] active:scale-95 transition-all"
      >
        <Bell size={20} strokeWidth={1.9} />
        {unread > 0 && (
          <span className="absolute top-2 right-2 w-2 h-2 rounded-full bg-[#CBA33B] ring-2 ring-white dark:ring-[#0D1520]" />
        )}
      </button>
    );
  }

  return (
    <div className={`relative ${isDesktopRow || isDrawerRow ? 'w-full' : ''}`}>
      {trigger}

      {/* ============ Popover ============
        Rendered into document.body via a portal so the popover ALWAYS
        anchors to the viewport — even when the trigger lives inside an
        ancestor with a CSS `transform` (e.g. the sliding hamburger drawer).
        Without the portal, browsers re-parent `position: fixed` to that
        transformed ancestor, which causes the popover to drift off-screen.
      */}
      {open && createPortal(
        <>
          {/* Backdrop — full-screen scrim on mobile, plain click-trap on desktop */}
          <div
            className="fixed inset-0 z-[59] bg-black/40 backdrop-blur-sm md:bg-transparent md:backdrop-blur-0"
            onClick={() => setOpen(false)}
          />
          <div
            ref={popoverRef}
            data-testid="notification-popover"
            className={`
              z-[60]
              ${getPopoverPositionClasses(variant)}
              w-[calc(100vw-1rem)] sm:w-[400px] max-w-[400px]
              max-h-[78vh] sm:max-h-[560px]
              flex flex-col
              rounded-2xl overflow-hidden
              bg-white dark:bg-[#101924]
              border border-slate-200 dark:border-white/[0.06]
              shadow-2xl shadow-black/30
              backdrop-blur-xl
              animate-bell-fade-in
            `}
            style={{
              ...computeAnchoredStyle(buttonRef.current, variant),
              boxShadow: '0 20px 60px -10px rgba(0,0,0,0.45), 0 0 0 1px rgba(203,163,59,0.08)',
            }}
          >
            {/* Header */}
            <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-slate-100 dark:border-white/[0.05]">
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-7 h-7 rounded-lg bg-[#CBA33B]/12 flex items-center justify-center">
                  <Bell size={14} className="text-[#B98A2C] dark:text-[#CBA33B]" strokeWidth={2.2} />
                </div>
                <h3 className="text-[14px] font-bold tracking-tight text-slate-900 dark:text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  Notifications
                </h3>
                {unread > 0 && (
                  <span className="ml-1 px-1.5 min-w-[18px] h-[18px] inline-flex items-center justify-center rounded-full bg-[#CBA33B]/15 text-[#B98A2C] dark:text-[#CBA33B] text-[10px] font-bold">
                    {unread}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1">
                {unread > 0 && (
                  <button
                    data-testid="notification-mark-all-read"
                    onClick={onMarkAllRead}
                    className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 hover:text-[#B98A2C] dark:hover:text-[#CBA33B] px-2 py-1 rounded transition-colors"
                  >
                    Mark all read
                  </button>
                )}
                <button
                  onClick={() => setOpen(false)}
                  className="md:hidden p-1 rounded text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-white/[0.05]"
                  aria-label="Close"
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto overscroll-contain">
              {loading && items.length === 0 ? (
                <SkeletonRows />
              ) : items.length === 0 ? (
                <EmptyState />
              ) : (
                <ul className="divide-y divide-slate-100 dark:divide-white/[0.04]">
                  {items.map((n) => (
                    <NotificationRow
                      key={n.id}
                      n={n}
                      onClick={() => onItemClick(n)}
                      onDelete={(e) => onDelete(e, n)}
                    />
                  ))}
                </ul>
              )}
            </div>

            {/* Footer hint */}
            <div className="px-4 py-2.5 border-t border-slate-100 dark:border-white/[0.05] bg-slate-50 dark:bg-white/[0.02]">
              <p className="text-[10px] text-slate-500 dark:text-slate-400 leading-tight">
                Manage which alerts appear here under{' '}
                <button
                  onClick={() => { setOpen(false); navigate('/settings#notifications'); }}
                  className="font-semibold text-[#B98A2C] dark:text-[#CBA33B] hover:underline"
                >
                  Settings → Notifications
                </button>
                .
              </p>
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
}

// =====================================================================
// Position helper — works against the viewport (popover lives in a portal
// at document.body so it never inherits a transformed ancestor).
// =====================================================================
function getPopoverPositionClasses(variant) {
  // Desktop sidebar variants (expanded row + collapsed icon) — the JS
  // `computeAnchoredStyle()` below provides absolute `top`/`left` from the
  // bell's bounding rect on md+. On mobile (drawer is fullscreen) we drop
  // back to a centered top sheet.
  if (variant === 'desktop-row' || variant === 'desktop-icon') {
    return 'fixed left-1/2 -translate-x-1/2 md:translate-x-0 top-[5vh] md:top-auto md:left-auto';
  }
  if (variant === 'drawer-row') {
    // Drawer sits over the mobile UI — center the sheet on screen.
    return 'fixed left-1/2 -translate-x-1/2 top-[5vh]';
  }
  // topbar default (mobile only — the desktop topbar never uses this).
  return 'fixed left-1/2 -translate-x-1/2 top-[5vh] sm:left-auto sm:translate-x-0 sm:right-3 sm:top-[3.8rem]';
}

/**
 * For desktop sidebar variants, compute an inline `top`/`left` style so the
 * popover sits *just right of the bell button* — exactly where the user
 * expects it to appear. We only apply it on md+ widths so the mobile
 * centered-sheet layout stays in charge below that breakpoint.
 */
function computeAnchoredStyle(triggerEl, variant) {
  if (!triggerEl) return {};
  if (variant !== 'desktop-row' && variant !== 'desktop-icon') return {};
  if (typeof window === 'undefined') return {};
  if (window.matchMedia && !window.matchMedia('(min-width: 768px)').matches) return {};
  const rect = triggerEl.getBoundingClientRect();
  // Anchor the popover so its bottom aligns with the bell's bottom and it
  // sits 12px to the right of the bell. Clamp so the popover never escapes
  // the viewport on smaller laptops.
  const left = Math.min(rect.right + 12, window.innerWidth - 412);
  const top = Math.max(12, Math.min(rect.bottom - 560, window.innerHeight - 580));
  return { left: `${left}px`, top: `${top}px` };
}

// =====================================================================
// Row
// =====================================================================
function NotificationRow({ n, onClick, onDelete }) {
  const icon = pickIcon(n.type);
  const tint = pickTint(n.accent);
  const isPayment = n.type === 'payment_received';
  return (
    <li>
      <button
        onClick={onClick}
        data-testid={`notification-item-${n.id}`}
        className={`group w-full px-4 py-3 flex items-start gap-3 text-left transition-colors
          ${n.read
            ? 'hover:bg-slate-50 dark:hover:bg-white/[0.02]'
            : isPayment
              ? 'bg-emerald-500/[0.06] hover:bg-emerald-500/[0.10] dark:bg-emerald-500/[0.08] dark:hover:bg-emerald-500/[0.12]'
              : 'bg-[#CBA33B]/[0.04] hover:bg-[#CBA33B]/[0.07] dark:bg-[#CBA33B]/[0.06] dark:hover:bg-[#CBA33B]/[0.10]'
          }
        `}
      >
        {/* Glyph tile — payments get a subtle ring to draw the eye. */}
        <span className={`mt-0.5 w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${tint.bg} ${
          isPayment ? 'ring-2 ring-emerald-500/30 dark:ring-emerald-400/30' : ''
        }`}>
          {React.createElement(icon, { size: isPayment ? 17 : 16, className: tint.fg, strokeWidth: isPayment ? 2.4 : 2 })}
        </span>

        {/* Text */}
        <span className="flex-1 min-w-0">
          <span className="block text-[13px] font-semibold leading-snug text-slate-900 dark:text-white truncate">
            {n.title}
          </span>
          {n.body && (
            <span className="block text-[11.5px] text-slate-500 dark:text-slate-400 mt-0.5 truncate">
              {n.body}
            </span>
          )}
          <span className="block text-[10.5px] text-slate-400 dark:text-slate-500 mt-1 font-medium tracking-wide uppercase">
            {timeAgo(n.created_at)}
          </span>
        </span>

        {/* Right side: unread dot + delete (hover) */}
        <span className="flex items-center gap-1.5 shrink-0 self-center">
          {!n.read && (
            <span className={`w-2 h-2 rounded-full ${
              isPayment
                ? 'bg-emerald-500 shadow-[0_0_0_3px_rgba(16,185,129,0.20)]'
                : 'bg-[#CBA33B] shadow-[0_0_0_3px_rgba(203,163,59,0.18)]'
            }`} />
          )}
          <span
            onClick={onDelete}
            role="button"
            tabIndex={0}
            aria-label="Dismiss"
            className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded text-slate-400 hover:text-red-500 hover:bg-red-500/10"
          >
            <Trash2 size={13} />
          </span>
        </span>
      </button>
    </li>
  );
}

// =====================================================================
// Empty state & skeletons
// =====================================================================
function EmptyState() {
  return (
    <div className="px-6 py-12 flex flex-col items-center text-center">
      <div className="w-14 h-14 rounded-2xl bg-[#CBA33B]/10 flex items-center justify-center mb-3">
        <Sparkles size={22} className="text-[#B98A2C] dark:text-[#CBA33B]" strokeWidth={1.7} />
      </div>
      <p className="text-[14px] font-semibold text-slate-900 dark:text-white">You&apos;re all caught up</p>
      <p className="text-[12px] text-slate-500 dark:text-slate-400 mt-1 max-w-[260px] leading-relaxed">
        Client activity — opened invoices, accepted estimates, signed documents — will show up right here.
      </p>
    </div>
  );
}

function SkeletonRows() {
  return (
    <ul className="divide-y divide-slate-100 dark:divide-white/[0.04]">
      {[0, 1, 2].map((i) => (
        <li key={i} className="px-4 py-3 flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-slate-100 dark:bg-white/[0.04] animate-pulse" />
          <div className="flex-1 space-y-1.5">
            <div className="h-3 w-3/4 rounded bg-slate-100 dark:bg-white/[0.04] animate-pulse" />
            <div className="h-2.5 w-1/2 rounded bg-slate-100 dark:bg-white/[0.04] animate-pulse" />
          </div>
        </li>
      ))}
    </ul>
  );
}

// =====================================================================
// Icon + tint pickers
//
// Every backend `ntype` should have its OWN glyph + tint so the user can
// scan the dropdown and instantly tell "money received" from "client
// just opened the invoice" without reading the headline. The most
// celebratory event — payment_received — gets the brightest treatment.
// =====================================================================
function pickIcon(type) {
  switch (type) {
    case 'payment_received':   return DollarSign;
    case 'estimate_accepted':  return Check;
    case 'document_signed':    return FileSignature;
    case 'invoice_delivered':  return Send;
    case 'invoice_clicked':    return MousePointerClick;
    case 'invoice_bounced':    return AlertTriangle;
    case 'coi_expiring':       return Shield;
    case 'invoice_opened':
    default:                   return Eye;
  }
}

// Some backend code uses 'green' (alias for emerald) — accept both so a
// new caller can't accidentally fall through to gold.
function pickTint(accent) {
  switch (accent) {
    case 'green':
    case 'emerald':
      return { bg: 'bg-emerald-500/15 dark:bg-emerald-500/12', fg: 'text-emerald-700 dark:text-emerald-300' };
    case 'sky':
      return { bg: 'bg-sky-500/15 dark:bg-sky-500/12', fg: 'text-sky-700 dark:text-sky-300' };
    case 'blue':
      return { bg: 'bg-blue-500/15 dark:bg-blue-500/12', fg: 'text-blue-700 dark:text-blue-300' };
    case 'rose':
      return { bg: 'bg-rose-500/15 dark:bg-rose-500/12', fg: 'text-rose-700 dark:text-rose-300' };
    case 'amber':
      return { bg: 'bg-amber-500/15 dark:bg-amber-500/12', fg: 'text-amber-700 dark:text-amber-300' };
    case 'gold':
    default:
      return { bg: 'bg-[#CBA33B]/15', fg: 'text-[#B98A2C] dark:text-[#CBA33B]' };
  }
}

// =====================================================================
// Time helper — "just now", "5m", "3h", "2d", or a short date
// =====================================================================
function timeAgo(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return '';
  const s = Math.floor(ms / 1000);
  if (s < 30) return 'Just now';
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}
